import { useEffect, useRef } from "react"
import Cookies from "js-cookie"
import posthog from "posthog-js"
import { PostHogProvider as PostHog } from "posthog-js/react"
import { env } from "../../config"
import { resolvePostHogConfig } from "./config"
import { flushPendingErrors } from "./capture-error"
import { getExceptionCaptureOptions } from "./exceptions"

declare global {
  interface Window {
    posthog: typeof posthog
    __FLAG_DATA__: any
  }
}

type PostHogWrapperProps = {
  children: React.ReactNode
}

export const PostHogProvider = ({ children }: PostHogWrapperProps) => {
  const debug = env("FF_DEBUG_POSTHOG") === "1" ? true : false
  const initialized = useRef(false)

  // Init in an effect rather than during render so posthog-js setup stays off
  // the hydration critical path.
  useEffect(() => {
    if (initialized.current) {
      return
    }

    // Fail closed when the key/host env is missing or baked in as the literal
    // string "undefined" — otherwise PostHog inits against /undefined/*
    // (MAR-662).
    const posthogConfig = resolvePostHogConfig(
      env("NEXT_PUBLIC_POSTHOG_KEY"),
      env("NEXT_PUBLIC_POSTHOG_HOST"),
    )

    if (!posthogConfig) {
      return
    }

    initialized.current = true

    const flags = Cookies.get("bootstrapData")

    let bootstrapData = {}

    if (flags) {
      // The cookie can come back truncated or malformed — that must not crash
      // the render; init without bootstrapped flags instead.
      try {
        bootstrapData = JSON.parse(flags)
      } catch {
        bootstrapData = {}
      }
    }

    // This provider sits above the app ErrorBoundary, so an init failure here
    // would take down the whole tree. PostHog is an enhancement, never fatal.
    try {
      posthog.init(posthogConfig.key, {
        api_host: posthogConfig.apiHost,
        bootstrap: bootstrapData,
        // Enables SPA route-aware pageview autocapture (capture_pageview:
        // 'history_change') + pageleave, so we no longer capture $pageview by hand.
        defaults: "2025-05-24",
        // No PostHog survey targets the marketing site (the live ones run in
        // the app), so skip downloading the surveys extension on every page.
        // Remove this before launching a survey here.
        disable_surveys: true,
        debug,
        ...getExceptionCaptureOptions(),
      })
      flushPendingErrors()
    } catch (error) {
      console.error("PostHog init failed:", error)

      return
    }

    if (!window.posthog) {
      window.posthog = posthog
      window.__FLAG_DATA__ = bootstrapData
    }
  }, [debug])

  return <PostHog client={posthog}>{children}</PostHog>
}
